import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useImperativeHandle,
  forwardRef,
} from 'react';
import { Input, DatePicker, Checkbox, message, Tooltip, Select } from 'kenshin';
import { FilterOutlined } from '@ant-design/icons';
import CustomSelect from '../../_tools/components/Select';
import moment from 'moment';
import './index.less';
import { selectPinyinFilter } from '@/_util';

const FilterPopup = ({ column, onFilter, onClose }) => {
  const [value, setValue] = useState(column.filterValue || '');

  const handleFilter = () => {
    onFilter(column.data, value);
    onClose();
  };

  const handleReset = () => {
    setValue('');
    onFilter(column.data, undefined);
    onClose();
  };

  let content = null;
  if (column.renderFilter) {
    content = column.renderFilter({
      value,
      onChange: setValue,
      onFilter: handleFilter,
      onReset: handleReset,
    });
  } else if (column.filterType === 'select') {
    let newV = value;
    if (column.filterProps?.mode === 'multiple' && !Array.isArray(value)) {
      newV = value?.split(',').filter(Boolean);
    }
    content = (
      <Select
        style={{ width: 180, marginBottom: 8 }}
        placeholder="请选择"
        value={newV}
        onChange={setValue}
        allowClear
        showSearch
        filterOption={(input, option) => {
          console.log('option?.label', option?.label);
          let str = option?.label ?? '';
          if (typeof option?.label === 'object') {
            str = str.props['data-label'];
          }
          console.log(selectPinyinFilter(str, input));

          // return selectPinyinFilter(str, input)
          return str?.toLowerCase().includes(input.toLowerCase());
          // return false
        }}
        dropdownStyle={{ zIndex: 2000 }}
        {...column.filterProps}
      />
    );
  } else {
    content = (
      <Input
        style={{ width: 180, marginBottom: 8 }}
        placeholder="请输入内容过滤"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onPressEnter={handleFilter}
        {...column.filterProps}
      />
    );
  }

  return (
    <div style={{ padding: 8 }}>
      {!column.renderFilter && <div style={{ marginBottom: 8 }}>{content}</div>}
      {column.renderFilter && content}
      {!column.renderFilter && (
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <a onClick={handleReset} style={{ fontSize: 12 }}>
            重置
          </a>
          <a onClick={handleFilter} style={{ fontSize: 12 }}>
            确定
          </a>
        </div>
      )}
    </div>
  );
};

const CellRenderer = ({
  hotInstance,
  renderer,
  rowIndex,
  colIndex,
  prop,
  value,
  cellProperties,
}) => {
  const ref = useRef(null);
  useEffect(() => {
    if (ref.current && renderer) {
      ref.current.innerHTML = '';
      renderer(
        hotInstance,
        ref.current,
        rowIndex,
        colIndex,
        prop,
        value,
        cellProperties,
      );
    }
  });

  return <div ref={ref} style={{ width: '100%', height: '100%' }} />;
};

const DateEditor = ({ value, onChange, onFinish, ...props }) => {
  const ref = useRef(null);
  const dropdownClass = 'simple-excel-date-picker-dropdown';
  const [isOpen, setIsOpen] = useState(true);

  const closeEditor = () => {
    setIsOpen(false);
    onFinish();
  };

  useEffect(() => {
    const handleMouseDown = (e) => {
      // Check if click is inside the editor wrapper
      if (ref.current && ref.current.contains(e.target)) {
        return;
      }
      // Check if click is inside the dropdown (portal) via our custom class
      if (e.target.closest(`.${dropdownClass}`)) {
        return;
      }
      // Fallback checks
      if (
        e.target.closest('.ant-picker-dropdown') ||
        e.target.closest('.ant-calendar-picker-container')
      ) {
        return;
      }
      // Clicked outside
      closeEditor();
    };

    document.addEventListener('mousedown', handleMouseDown);
    return () => {
      document.removeEventListener('mousedown', handleMouseDown);
    };
  }, [onFinish]);

  return (
    <div
      ref={ref}
      onMouseDown={(e) => e.stopPropagation()}
      style={{ width: '100%', height: '100%' }}
    >
      <DatePicker
        {...props}
        dropdownClassName={dropdownClass}
        popupClassName={dropdownClass}
        defaultValue={value ? moment(value) : null}
        onChange={(date, dateString) => {
          onChange(dateString);
        }}
        onSelect={(date) => {
          if (date) {
            onChange(date.format('YYYY-MM-DD HH:mm:ss'));
          }
        }}
        onOk={(date) => {
          if (date) {
            onChange(date.format('YYYY-MM-DD HH:mm:ss'));
          }
          closeEditor();
        }}
        onOpenChange={(open) => {
          if (!open) {
            closeEditor();
          }
        }}
        showTime
        format="YYYY-MM-DD HH:mm:ss"
        open={isOpen}
        getPopupContainer={(triggerNode) => document.body}
      />
    </div>
  );
};

const SimpleExcel = forwardRef(
  (
    {
      data = [],
      columns = [],
      onChange,
      height,
      disabled,
      activeTab,
      onRowRemove,
      onCellClick,
      onCopy,
      onPaste,
      onRowCopy,
      onRowMove,
      onRowRefer,
    },
    ref,
  ) => {
    const [editingCell, setEditingCell] = useState(null); // { row, col }
    const [cellValue, setCellValue] = useState(''); // Current editing cell value
    const [selectionStart, setSelectionStart] = useState(null); // { row, col }
    const [selectionEnd, setSelectionEnd] = useState(null); // { row, col }
    const [isSelecting, setIsSelecting] = useState(false);
    const [contextMenu, setContextMenu] = useState({
      visible: false,
      x: 0,
      y: 0,
      rowIndex: -1,
    });
    const tableRef = useRef(null);
    const metaDataRef = useRef({}); // Store cell meta data
    const [ignored, forceUpdate] = useState(0);
    const prevDataLength = useRef(data.length);
    const [filters, setFilters] = useState({});

    // Clear meta data when data length changes (e.g. add/remove row) to prevent mismatch
    useEffect(() => {
      if (data.length !== prevDataLength.current) {
        metaDataRef.current = {};
        prevDataLength.current = data.length;
        forceUpdate((x) => x + 1);
      }
    }, [data.length]);

    const handleFilter = (dataIndex, value) => {
      setFilters((prev) => {
        const newFilters = { ...prev };
        if (
          value === undefined ||
          value === '' ||
          value === null ||
          (Array.isArray(value) && value.length === 0)
        ) {
          delete newFilters[dataIndex];
        } else {
          newFilters[dataIndex] = value;
        }
        return newFilters;
      });
    };

    const filteredData = useMemo(() => {
      return data.filter((row) => {
        return Object.entries(filters).every(([key, value]) => {
          let cellValue = row[key];
          if (cellValue == undefined) return false;
          if (typeof cellValue === 'string' && cellValue.includes(','))
            cellValue = cellValue.split(',');
          if (value === undefined || value === null || value === '')
            return true;

          // Handle array values (multi-select)
          if (Array.isArray(value)) {
            if (
              cellValue === undefined ||
              cellValue === null ||
              cellValue === ''
            ) {
              return false;
            }
            // If filter is array, check if cell value is in array or intersects
            if (Array.isArray(cellValue)) {
              // Check intersection for multi-select cells vs multi-select filter
              // If any item in cellValue is in value, keep it.
              // Assuming value is array of IDs/Strings
              return cellValue.some((item) => {
                const itemVal =
                  item && typeof item === 'object' && 'value' in item
                    ? item.value
                    : item;
                return value.some(
                  (filterVal) => String(filterVal) === String(itemVal),
                );
              });
            }
            // Single value cell vs multi-select filter
            const cellVal =
              cellValue && typeof cellValue === 'object' && 'value' in cellValue
                ? cellValue.value
                : cellValue;
            return value.some(
              (filterVal) => String(filterVal) === String(cellVal),
            );
          }

          const colDef = columns.find((col) => col.data === key);

          if (typeof cellValue === 'string' || typeof cellValue === 'number') {
            if (colDef && colDef.filterType === 'select') {
              return String(cellValue) === String(value);
            }
            return String(cellValue)
              .toLowerCase()
              .includes(String(value).toLowerCase());
          }
          // Handle object values if needed (e.g. {label, value})
          if (cellValue && typeof cellValue === 'object' && cellValue.value) {
            if (colDef && colDef.filterType === 'select') {
              return String(cellValue.value) === String(value);
            }
            return String(cellValue.value)
              .toLowerCase()
              .includes(String(value).toLowerCase());
          }

          return true;
        });
      });
    }, [data, filters, columns]);

    // Update cellValue when editingCell changes
    useEffect(() => {
      if (editingCell) {
        const { row, col } = editingCell;
        setCellValue(getValue(row, col));
      }
    }, [editingCell, filteredData, columns]); // changed data to filteredData

    // Close context menu on click outside
    useEffect(() => {
      const handleClick = () => {
        if (contextMenu.visible) {
          setContextMenu({ ...contextMenu, visible: false });
        }
      };
      document.addEventListener('click', handleClick);
      return () => {
        document.removeEventListener('click', handleClick);
      };
    }, [contextMenu]);

    // Helper to get value from data safely
    const getValue = (rowIndex, colIndex) => {
      const colDef = columns[colIndex];
      const rowData = filteredData[rowIndex]; // Use filteredData
      if (!rowData || !colDef) return '';
      return rowData[colDef.data];
    };

    // Helper to handle value change
    const handleChange = (rowIndex, colIndex, newValue, source = 'edit') => {
      if (colIndex == -1) return;
      const colDef = columns[colIndex];
      const rowData = filteredData[rowIndex]; // Use filteredData to find original row

      // We need to find the index in the original data array
      const originalRowIndex = data.indexOf(rowData);

      const oldValue = getValue(rowIndex, colIndex);

      if (onChange && originalRowIndex !== -1) {
        // Handsontable format: [[row, prop, oldValue, newValue]]
        onChange([[originalRowIndex, colDef.data, oldValue, newValue]], source);
      }
    };

    const handleHeaderCheckboxChange = (colIndex, e) => {
      const checked = e.target.checked;
      const colDef = columns[colIndex];
      const changes = [];

      filteredData.forEach((row) => {
        const originalRowIndex = data.indexOf(row);
        if (originalRowIndex !== -1) {
          const oldValue = row[colDef.data];
          const newValue = checked ? 1 : 0;
          if (!!oldValue !== checked) {
            changes.push([originalRowIndex, colDef.data, oldValue, newValue]);
          }
        }
      });

      if (changes.length > 0 && onChange) {
        onChange(changes, 'header-checkbox');
      }
    };

    // Define hotInstance to be passed to CustomSelect and exposed via ref
    const hotInstance = useMemo(
      () => ({
        setDataAtCell: (row, colIndex, value, source = 'api') => {
          // row here might be index in filtered data if called from UI interaction on filtered view,
          // but typically setDataAtCell is used programmatically.
          // If we assume it works on visual rows:
          handleChange(row, colIndex, value, source);
        },
        getCellMeta: (row, colIndex) => {
          const key = `${row}-${colIndex}`;
          if (!metaDataRef.current[key]) metaDataRef.current[key] = {};
          return metaDataRef.current[key];
        },
        render: () => {
          forceUpdate((x) => x + 1);
        },
        getDataAtCell: (row, colIndex) => {
          return getValue(row, colIndex);
        },
        getSourceDataAtRow: (row) => {
          return filteredData[row]; // Use filteredData
        },
        getSourceData: () => {
          return filteredData; // Use filteredData
        },
        propToCol: (prop) => {
          return columns.findIndex((col) => col.data === prop);
        },
        getSettings: () => ({
          columns: columns,
        }),
      }),
      [data, filteredData, columns, onChange], // Added filteredData
    ); // Dependencies for closure variables

    useImperativeHandle(ref, () => ({
      hotInstance,
    }));

    // Handle Copy/Paste
    useEffect(() => {
      const handleCopy = (e) => {
        if (!selectionStart || !selectionEnd) return;
        // Prevent default copy if we are in an input (editing)
        if (editingCell) return;

        const startRow = Math.min(selectionStart.row, selectionEnd.row);
        const endRow = Math.max(selectionStart.row, selectionEnd.row);
        const startCol = Math.min(selectionStart.col, selectionEnd.col);
        const endCol = Math.max(selectionStart.col, selectionEnd.col);

        if (onCopy) {
          const range = { startRow, endRow, startCol, endCol };
          const handled = onCopy(range);
          if (handled) {
            e.preventDefault();
            return;
          }
        }

        e.preventDefault();

        const rows = [];
        for (let r = startRow; r <= endRow; r++) {
          const rowData = [];
          for (let c = startCol; c <= endCol; c++) {
            const val = getValue(r, c);
            rowData.push(val);
          }
          rows.push(rowData.join('\t'));
        }
        const text = rows.join('\n');
        if (navigator.clipboard) {
          navigator.clipboard.writeText(text);
        }
      };

      const handlePaste = async (e) => {
        if (!selectionStart) return;
        if (editingCell) return; // Let input handle paste

        e.preventDefault();

        const text = (e.clipboardData || window.clipboardData).getData('text');
        if (!text) return;

        const startRow = selectionStart.row;
        const endRow = selectionEnd ? selectionEnd.row : startRow;
        const startCol = selectionStart.col;
        const endCol = selectionEnd ? selectionEnd.col : startCol;

        if (onPaste) {
          const range = { startRow, endRow, startCol, endCol };
          const handled = onPaste(text, range);
          if (handled) return;
        }

        const rows = text.split(/\r\n|\n|\r/).map((row) => row.split('\t'));
        if (rows.length === 0) return;

        const changes = [];

        rows.forEach((row, rIdx) => {
          row.forEach((cellValue, cIdx) => {
            const targetRow = startRow + rIdx;
            const targetCol = startCol + cIdx;

            if (targetRow < filteredData.length && targetCol < columns.length) {
              // Check against filteredData
              const colDef = columns[targetCol];
              if (!colDef.readOnly) {
                const oldValue = getValue(targetRow, targetCol);
                let val = cellValue;
                if (val.startsWith('"') && val.endsWith('"')) {
                  val = val.substring(1, val.length - 1);
                }

                const currentRowData = filteredData[targetRow];
                const originalRowIndex = data.indexOf(currentRowData);

                if (originalRowIndex !== -1) {
                  changes.push([originalRowIndex, colDef.data, oldValue, val]);
                }
              }
            }
          });
        });

        if (changes.length > 0 && onChange) {
          onChange(changes, 'paste');
        }
      };

      const container = document.querySelector('.simple-excel-container');
      if (container) {
        // container.addEventListener('copy', handleCopy);
        // container.addEventListener('paste', handlePaste);
      }

      return () => {
        if (container) {
          // container.removeEventListener('copy', handleCopy);
          // container.removeEventListener('paste', handlePaste);
        }
      };
    }, [
      selectionStart,
      selectionEnd,
      editingCell,
      data,
      filteredData,
      columns,
      onChange,
      onCopy,
      onPaste,
    ]);

    const handleCellClick = (rowIndex, colIndex, row) => {
      if (disabled) return;
      if (onCellClick) {
        onCellClick(rowIndex, colIndex, row);
      }
      const colDef = columns[colIndex];
      if (colDef.readOnly) return;

      // Save any current textarea value before starting new edit
      saveTextareaValue();
      setEditingCell({ row: rowIndex, col: colIndex });
      setSelectionStart({ row: rowIndex, col: colIndex });
      setSelectionEnd({ row: rowIndex, col: colIndex });
    };

    const handleMouseDown = (e, rowIndex, colIndex) => {
      if (disabled) return;
      if (e.button !== 0) return; // Only left click

      // Save any current textarea value before starting selection
      saveTextareaValue();
      setIsSelecting(true);
      setSelectionStart({ row: rowIndex, col: colIndex });
      setSelectionEnd({ row: rowIndex, col: colIndex });
      setEditingCell(null); // Clear editing when starting selection
    };

    const handleMouseOver = (rowIndex, colIndex) => {
      if (isSelecting) {
        setSelectionEnd({ row: rowIndex, col: colIndex });
      }
    };

    const handleMouseUp = () => {
      setIsSelecting(false);
    };

    // Add global mouse up to stop selecting if mouse released outside
    useEffect(() => {
      const handleGlobalMouseUp = () => {
        setIsSelecting(false);
      };
      window.addEventListener('mouseup', handleGlobalMouseUp);
      return () => {
        window.removeEventListener('mouseup', handleGlobalMouseUp);
      };
    }, []);

    const isSelected = (rowIndex, colIndex) => {
      if (!selectionStart || !selectionEnd) return false;
      const startRow = Math.min(selectionStart.row, selectionEnd.row);
      const endRow = Math.max(selectionStart.row, selectionEnd.row);
      const startCol = Math.min(selectionStart.col, selectionEnd.col);
      const endCol = Math.max(selectionStart.col, selectionEnd.col);

      return (
        rowIndex >= startRow &&
        rowIndex <= endRow &&
        colIndex >= startCol &&
        colIndex <= endCol
      );
    };

    const handleContextMenu = (e, rowIndex) => {
      e.preventDefault();
      if (disabled) return;
      // Save any current textarea value before showing context menu
      saveTextareaValue();
      setContextMenu({
        visible: true,
        x: e.clientX,
        y: e.clientY,
        rowIndex,
      });
    };

    // Save textarea value before clearing editing state
    const saveTextareaValue = () => {
      if (editingCell) {
        const { row, col } = editingCell;
        handleChange(row, col, cellValue);
      }
    };

    const renderCellEditor = (rowIndex, colIndex) => {
      const colDef = columns[colIndex];

      const onBlur = () => {
        handleChange(rowIndex, colIndex, cellValue);
        setEditingCell(null);
      };

      const commonProps = {
        autoFocus: true,
        size: 'small',
        onBlur: onBlur,
        style: { width: '100%', height: '100%', borderRadius: 0 },
      };

      const stopPropagation = (e) => e.stopPropagation();

      if (colDef.type === 'date') {
        // Remove onBlur from commonProps to prevent closing when clicking inside the picker panel
        const { onBlur, ...dateProps } = commonProps;
        return (
          <DateEditor
            {...dateProps}
            value={getValue(rowIndex, colIndex)}
            onChange={(val) => handleChange(rowIndex, colIndex, val)}
            onFinish={() => setEditingCell(null)}
          />
        );
      }

      if (colDef.type === 'checkbox') {
        return null;
      }

      return (
        <div
          onMouseDown={stopPropagation}
          style={{ width: '100%', minHeight: '100%' }}
        >
          <Input.TextArea
            {...commonProps}
            style={{ ...commonProps.style, height: 'auto', minHeight: '100%' }}
            value={cellValue}
            autoSize
            onChange={(e) => setCellValue(e.target.value)}
            onPressEnter={(e) => {
              if (e.shiftKey) return;
              e.preventDefault();
              handleChange(rowIndex, colIndex, cellValue);
              onBlur();
            }}
            onBlur={(e) => {
              handleChange(rowIndex, colIndex, cellValue);
              onBlur();
            }}
          />
        </div>
      );
    };

    const renderCellContent = (rowIndex, colIndex, colDef, value) => {
      if (colDef.renderer) {
        if (colDef.title === '上报') {
          return (
            <div
              className="table-option-btn"
              onClick={() => {
                const rowData = filteredData[rowIndex]; // Use filteredData
                console.log('点上报', rowData);
                if (!rowData.taskId) {
                  message.error('请先设置任务、项目信息');
                  return;
                }
                const taskData = {
                  taskId: rowData.taskId,
                  taskName: rowData.taskName,
                  projectId: rowData.projectId,
                  projectName: rowData.projectName,
                  proServCatgId: rowData._proServCatgId,
                  servCatgName: rowData.proServCatgId,
                  taskBeginTime: rowData.taskBeginTime,
                  taskEndTime: rowData.taskEndTime,
                  taskStatus: rowData.taskStatus,
                  taskRemark: rowData.taskRemark,
                  contractNumber: rowData.contractNumber,
                };
                if (window.showRptModal) {
                  window.showRptModal(taskData);
                }
              }}
            >
              点击上报
            </div>
          );
        }
        return (
          <CellRenderer
            hotInstance={hotInstance}
            renderer={colDef.renderer}
            rowIndex={rowIndex}
            colIndex={colIndex}
            prop={colDef.data}
            value={value}
            cellProperties={{ readOnly: colDef.readOnly }}
          />
        );
      }

      if (colDef.type === 'checkbox') {
        return (
          <Checkbox
            key={`${rowIndex}-${colIndex}-${value}`}
            checked={!!value}
            disabled={colDef.readOnly || disabled}
            onChange={(e) =>
              handleChange(rowIndex, colIndex, e.target.checked ? 1 : 0)
            }
          />
        );
      }

      if (colDef.type === 'singleSelect' || colDef.type === 'multiSelect') {
        const meta = hotInstance.getCellMeta(rowIndex, colIndex);
        const source = meta.source || colDef.source || [];

        return (
          <CustomSelect
            key={`${rowIndex}-${colIndex}-${value}`}
            options={source}
            value={value || value === 0 ? value.toString() : ''}
            onChange={(val) => handleChange(rowIndex, colIndex, val)}
            row={rowIndex}
            col={colIndex}
            prop={colDef.data}
            multiple={colDef.type === 'multiSelect'}
            hotInstance={hotInstance}
            disabled={colDef.readOnly || disabled}
            hasInput={colDef.hasInput}
            filterInp={colDef.filterInp}
            columns={columns}
          />
        );
      }

      return value;
    };

    const fixedColumnsCount = 4;

    const columnWidths = useMemo(() => {
      let currentLeft = 0;
      return columns.map((col) => {
        const width = parseInt(col.width || 100, 10);
        const left = currentLeft;
        if (!col.hidden) {
          currentLeft += width;
        }
        return { width, left };
      });
    }, [columns]);

    const visibleColumns = columns
      .map((col, index) => ({ ...col, originalIndex: index }))
      .filter((col) => !col.hidden);

    const totalWidth = visibleColumns.reduce(
      (sum, col) => sum + parseInt(col.width || 100, 10),
      0,
    );

    return (
      <div className="simple-excel-container" style={{ height }}>
        {contextMenu.visible && (
          <div
            className="context-menu"
            style={{
              position: 'fixed',
              top: contextMenu.y,
              left: contextMenu.x,
              zIndex: 1000,
              background: '#fff',
              boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
              border: '1px solid #eee',
              padding: '4px 0',
              borderRadius: '2px',
              cursor: 'pointer',
            }}
          >
            {onRowCopy && (
              <div
                style={{ padding: '5px 12px', fontSize: '14px', color: '#333' }}
                className="context-menu-item"
                onClick={() => {
                  if (onRowCopy) {
                    onRowCopy(contextMenu.rowIndex);
                  }
                  setContextMenu({ ...contextMenu, visible: false });
                }}
              >
                复制
              </div>
            )}
            {onRowMove && (
              <div
                style={{ padding: '5px 12px', fontSize: '14px', color: '#333' }}
                className="context-menu-item"
                onClick={() => {
                  if (onRowMove) {
                    onRowMove(contextMenu.rowIndex);
                  }
                  setContextMenu({ ...contextMenu, visible: false });
                }}
              >
                移动
              </div>
            )}
            {onRowRefer && (
              <div
                style={{ padding: '5px 12px', fontSize: '14px', color: '#333' }}
                className="context-menu-item"
                onClick={() => {
                  if (onRowRefer) {
                    onRowRefer(contextMenu.rowIndex);
                  }
                  setContextMenu({ ...contextMenu, visible: false });
                }}
              >
                引用
              </div>
            )}
            <div
              style={{ padding: '5px 12px', fontSize: '14px', color: '#333' }}
              className="context-menu-item"
              onClick={() => {
                if (onRowRemove) {
                  onRowRemove(contextMenu.rowIndex, 1);
                }
                setContextMenu({ ...contextMenu, visible: false });
              }}
            >
              删除行
            </div>
          </div>
        )}
        <div className="simple-excel-table">
          <table style={{ width: totalWidth, minWidth: '100%' }}>
            <colgroup>
              {visibleColumns.map((col, idx) => (
                <col key={idx} style={{ width: col.width || 100 }} />
              ))}
            </colgroup>
            <thead>
              <tr>
                {visibleColumns.map((col, idx) => {
                  const isFixed = idx < fixedColumnsCount;
                  const isLastFixed = idx === fixedColumnsCount - 1;
                  const style = isFixed
                    ? {
                        left: columnWidths[col.originalIndex].left,
                        zIndex: 15,
                      }
                    : {};

                  return (
                    <th
                      key={idx}
                      className={`${isFixed ? 'fixed-col' : ''} ${
                        isLastFixed ? 'last-fixed' : ''
                      }`}
                      style={style}
                    >
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          height: '100%',
                        }}
                      >
                        <div
                          style={{
                            overflow: 'hidden',
                            flex: 1,
                          }}
                        >
                          {col.selectAll ? (
                            <Checkbox
                              checked={
                                filteredData.length > 0 &&
                                filteredData.every((row) => !!row[col.data])
                              }
                              indeterminate={
                                filteredData.some((row) => !!row[col.data]) &&
                                !filteredData.every((row) => !!row[col.data])
                              }
                              disabled={disabled}
                              onChange={(e) =>
                                handleHeaderCheckboxChange(col.originalIndex, e)
                              }
                              onClick={(e) => e.stopPropagation()}
                            />
                          ) : (
                            <span
                              style={{
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                              title={
                                typeof col.title === 'string' ? col.title : ''
                              }
                            >
                              {col.title}
                            </span>
                          )}
                        </div>
                        {col.filter && (
                          <Tooltip
                            trigger="click"
                            title={
                              <FilterPopup
                                column={col}
                                onFilter={handleFilter}
                                onClose={() => document.body.click()} // Close popover by simulating click outside or use controlled popover if needed
                              />
                            }
                            placement="bottomRight"
                            overlayStyle={{ maxWidth: 'none' }}
                            overlayClassName="filter-tooltip"
                          >
                            <FilterOutlined
                              style={{
                                cursor: 'pointer',
                                fontSize: 12,
                                color: filters[col.data]
                                  ? '#1890ff'
                                  : '#bfbfbf',
                                marginLeft: 4,
                              }}
                              onClick={(e) => e.stopPropagation()}
                            />
                          </Tooltip>
                        )}
                      </div>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {filteredData.map((row, rowIndex) => (
                <tr key={rowIndex}>
                  {visibleColumns.map((col, idx) => {
                    const colIndex = col.originalIndex;
                    const isFixed = idx < fixedColumnsCount;
                    const isLastFixed = idx === fixedColumnsCount - 1;
                    const style = isFixed
                      ? {
                          left: columnWidths[colIndex].left,
                        }
                      : {};

                    const isEditing =
                      editingCell &&
                      editingCell.row === rowIndex &&
                      editingCell.col === colIndex;
                    const value = getValue(rowIndex, colIndex);
                    const shouldUseCustomComponent =
                      col.type === 'singleSelect' ||
                      col.type === 'multiSelect' ||
                      col.type === 'checkbox';

                    return (
                      <td
                        key={idx}
                        className={`${isFixed ? 'fixed-col' : ''} ${
                          isLastFixed ? 'last-fixed' : ''
                        } ${
                          isSelected(rowIndex, colIndex) ? 'cell-selected' : ''
                        }`}
                        style={style}
                        onClick={() => handleCellClick(rowIndex, colIndex, row)}
                        onMouseDown={(e) =>
                          handleMouseDown(e, rowIndex, colIndex)
                        }
                        onMouseOver={() => handleMouseOver(rowIndex, colIndex)}
                        onContextMenu={(e) => handleContextMenu(e, rowIndex)}
                      >
                        {isEditing &&
                        !col.readOnly &&
                        !shouldUseCustomComponent ? (
                          <div className="cell-editor">
                            {renderCellEditor(rowIndex, colIndex)}
                          </div>
                        ) : (
                          <div
                            className="cell-content"
                            title={typeof value === 'string' ? value : ''}
                          >
                            {renderCellContent(rowIndex, colIndex, col, value)}
                          </div>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  },
);

export default SimpleExcel;
